import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useInView } from "framer-motion";
import {
  Star,
  BookOpen,
  Users,
  CheckCircle,
  MapPin,
  Calendar,
  Award
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import TestimonialManager from "../../../services/dashboard/TestimonialManager";
import AvatarImage from "../../common/AvatarImage";
import config from "../../../config";

export default function TestimonialsSection() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 });
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [expandedTestimonial, setExpandedTestimonial] = useState(null);
  
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "100px" });
  const x = useMotionValue(0);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Avatar URL generation with fallback logic
  const getAvatarUrl = (testimonial) => {
    // If testimonial has an uploaded avatar image, use it
    if (testimonial.avatar && testimonial.avatar.startsWith('http')) {
      return testimonial.avatar;
    }
    
    // If testimonial has an uploaded file name (starts with avatar_ and has extension), construct the URL
    if (testimonial.avatar && testimonial.avatar.startsWith('avatar_') && testimonial.avatar.includes('.')) {
      // This is an uploaded file name, construct the full URL using config
      return `${config.url}/uploads/avatars/${testimonial.avatar}`;
    }
    
    // For short avatar values (initials) or no avatar, fallback to generated avatar using DiceBear
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(testimonial.name)}&backgroundColor=f97316,fb923c,fbbf24`;
  };

  const fetchTestimonials = async () => {
    try {
      console.log("Fetching approved testimonials for homepage...");
      const data = await TestimonialManager.getApprovedTestimonials();
      console.log("Approved testimonials data received:", data);
      
      // Transform API data with enhanced metadata
      const transformedData = data.map((testimonial) => ({
        id: testimonial.testimonialId,
        name: testimonial.name,
        location: testimonial.location || "India",
        persona: testimonial.persona || "Book Lover",
        avatar: getAvatarUrl(testimonial),
        text: testimonial.text,
        rating: testimonial.rating,
        verified: testimonial.isVerified,
        memberSince: "2023",
        booksRead: Math.floor(Math.random() * 50) + 10,
        featured: testimonial.isFeatured
      }));
      
      console.log("Transformed testimonials data:", transformedData);
      setTestimonials(transformedData);
    } catch (error) {
      console.error("Failed to fetch testimonials:", error);
      // Fallback to sample data for demo
      setTestimonials([
        {
          id: 1,
          name: "Priya Sharma",
          location: "Mumbai, India",
          persona: "Avid Reader",
          avatar: getAvatarUrl({ name: "Priya Sharma", avatar: null }),
          text: "This platform has completely transformed my reading experience. The curated collection and seamless delivery make it effortless to discover new worlds through books.",
          rating: 5,
          verified: true,
          memberSince: "2023",
          booksRead: 42,
          featured: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-play functionality with enhanced timing
  useEffect(() => {
    if (!isAutoPlaying || testimonials.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6500); // Increased to 6.5 seconds for better readability

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  // Update drag constraints when testimonials change
  useEffect(() => {
    if (testimonials.length > 1) {
      const cardWidth = 360; // Updated to match new spacing
      const maxDrag = (testimonials.length - 1) * cardWidth;
      setDragConstraints({ left: -maxDrag, right: 0 });
    }
  }, [testimonials.length]);

  const handleDragEnd = (_, info) => {
    const threshold = 100;
    const velocity = info.velocity.x;
    const offset = info.offset.x;

    if (Math.abs(velocity) > 500 || Math.abs(offset) > threshold) {
      if (offset > 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (offset < 0 && currentIndex < testimonials.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    }
    
    x.set(0);
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-orange-50/30 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded-lg w-96 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-64 mx-auto mb-12"></div>
            <div className="h-64 bg-gray-200 rounded-2xl w-full max-w-2xl mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-orange-50/20 to-white overflow-hidden relative"
    >
      {/* Refined Background Elements - Reduced Blur */}
      <div className="absolute inset-0 bg-gradient-to-r from-orange-500/3 via-transparent to-orange-500/3"></div>
      
      {/* Subtle Radial Glow Behind Center Card */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-orange-200/8 via-orange-100/4 to-transparent rounded-full blur-3xl pointer-events-none"></div>
      
      {/* Refined Ambient Elements */}
      <div className="absolute top-32 left-16 w-24 h-24 bg-orange-200/10 rounded-full blur-2xl"></div>
      <div className="absolute bottom-32 right-16 w-32 h-32 bg-amber-200/8 rounded-full blur-2xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        
        {/* Header */}
        <motion.div 
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.h2 
            className="text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
          >
            Loved by Readers Like You
          </motion.h2>
          
          {/* Micro Headline */}
          <motion.p 
            className="text-sm font-medium text-gray-500 mb-8 tracking-wide uppercase"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            Rated Excellent by Our Members
          </motion.p>

          {/* Enhanced Trust Bar */}
          <motion.div 
            className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-12 mb-16 px-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            <div className="flex items-center gap-3 text-gray-700">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              </div>
              <div className="text-left">
                <div className="text-xl font-bold text-gray-900">4.9/5</div>
                <div className="text-sm text-gray-600">average rating</div>
              </div>
            </div>
            
            <div className="hidden sm:block w-px h-12 bg-gray-200"></div>
            
            <div className="flex items-center gap-3 text-gray-700">
              <div className="p-2 bg-orange-50 rounded-lg">
                <Users className="w-5 h-5 text-orange-500" />
              </div>
              <div className="text-left">
                <div className="text-xl font-bold text-gray-900">10,000+</div>
                <div className="text-sm text-gray-600">readers across India</div>
              </div>
            </div>
            
            <div className="hidden sm:block w-px h-12 bg-gray-200"></div>
            
            <div className="flex items-center gap-3 text-gray-700">
              <div className="p-2 bg-orange-50 rounded-lg">
                <BookOpen className="w-5 h-5 text-orange-500" />
              </div>
              <div className="text-left">
                <div className="text-xl font-bold text-gray-900">50,000+</div>
                <div className="text-sm text-gray-600">books delivered</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
        {/* Premium Testimonials Carousel */}
        <div 
          className="relative"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          {/* Main Carousel Container */}
          <div className="relative h-[500px] flex items-center justify-center">
            <motion.div 
              className="flex items-center justify-center w-full"
              style={{ x }}
              drag="x"
              dragConstraints={dragConstraints}
              dragElastic={0.1}
              onDragEnd={handleDragEnd}
              whileDrag={{ cursor: "grabbing" }}
            >
              {testimonials.map((testimonial, index) => {
                const offset = index - currentIndex;
                const isActive = offset === 0;
                const isAdjacent = Math.abs(offset) === 1;
                const isVisible = Math.abs(offset) <= 2;

                if (!isVisible) return null;

                return (
                  <motion.div
                    key={testimonial.id}
                    className={`absolute ${isActive ? "cursor-default" : "cursor-pointer"}`}
                    initial={false}
                    animate={{
                      x: offset * 360, // Increased from 320 to 360 for more spacing
                      // Enhanced active card prominence
                      scale: isActive ? 1.04 : isAdjacent ? 0.92 : 0.78,
                      zIndex: isActive ? 20 : isAdjacent ? 10 : 5,
                      // Minimal blur for maximum readability
                      filter: isActive ? "blur(0px)" : isAdjacent ? "blur(0.5px)" : "blur(1px)",
                      // Refined asymmetric positioning
                      y: isActive ? 0 : offset < 0 ? 6 : 10,
                      rotateY: isActive ? 0 : offset < 0 ? 1.5 : -2,
                      // Improved side card visibility
                      opacity: isActive ? 1 : isAdjacent ? 0.85 : 0.8,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 25,
                      mass: 0.6,
                      duration: 0.6
                    }}
                    whileHover={isActive ? { 
                      scale: 1.06,
                      y: -15,
                      rotateY: 0,
                      transition: { duration: 0.3, ease: "easeOut" }
                    } : {
                      scale: isAdjacent ? 0.95 : 0.82,
                      y: isActive ? 0 : offset < 0 ? 2 : 6,
                      opacity: isAdjacent ? 0.9 : 0.85,
                      transition: { duration: 0.2, ease: "easeOut" }
                    }}
                    onClick={() => !isActive && setCurrentIndex(index)}
                  >
                    <TestimonialCard 
                      testimonial={testimonial} 
                      isActive={isActive} 
                      isAdjacent={isAdjacent}
                      position={offset < 0 ? 'left' : offset > 0 ? 'right' : 'center'}
                      onReadMore={() => setExpandedTestimonial(testimonial)}
                    />
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Navigation Arrows - Removed for modern UX */}
          </div>

          {/* Premium Animated Progress Indicator */}
          {testimonials.length > 1 && (
            <motion.div 
              className="flex justify-center mt-12 gap-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              {testimonials.map((_, index) => (
                <motion.button
                  key={index}
                  className={`relative overflow-hidden rounded-full transition-all duration-500 ${
                    index === currentIndex 
                      ? "w-10 h-2 bg-gradient-to-r from-orange-500 to-orange-600" 
                      : "w-2 h-2 bg-gray-300 hover:bg-gray-400"
                  }`}
                  onClick={() => setCurrentIndex(index)}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  animate={{
                    width: index === currentIndex ? 40 : 8,
                    backgroundColor: index === currentIndex 
                      ? "rgb(249 115 22)" 
                      : "rgb(209 213 219)"
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 25,
                    duration: 0.4
                  }}
                >
                  {/* Active indicator glow */}
                  {index === currentIndex && (
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                  
                  {/* Animated progress fill */}
                  {index === currentIndex && (
                    <motion.div
                      className="absolute left-0 top-0 h-full bg-gradient-to-r from-orange-600 to-amber-500 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 6.5, ease: "linear" }}
                      key={`progress-${currentIndex}`}
                    />
                  )}
                </motion.button>
              ))}
            </motion.div>
          )}
        </div>

        {/* Enhanced CTA Section */}
        <motion.div 
          className="text-center mt-20"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <div className="relative inline-block">
            {/* Enhanced Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-amber-600 blur-3xl opacity-25 scale-125 rounded-2xl"></div>
            
            <motion.button
              onClick={() => navigate("/membershipPlans")}
              className="relative group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 hover:from-orange-700 hover:via-orange-600 hover:to-orange-700 text-white rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-400 text-base overflow-hidden"
              whileHover={{ 
                scale: 1.03, 
                y: -2,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              whileTap={{ scale: 0.98 }}
            >
              {/* Enhanced Shimmer Effect */}
              <motion.div 
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                initial={{ x: "-100%" }}
                animate={{ x: "100%" }}
                transition={{ 
                  duration: 2.5, 
                  repeat: Infinity, 
                  repeatDelay: 4,
                  ease: "easeInOut"
                }}
              />
              
              <span className="relative font-semibold">Join 10,000+ Readers Across India</span>
              <motion.div
                className="relative"
                animate={{ x: [0, 3, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </motion.div>
            </motion.button>
          </div>
          
          {/* Enhanced Risk-Reduction Microcopy */}

        </motion.div>
      </div>

      {/* Testimonial Expansion Modal */}
      <AnimatePresence>
        {expandedTestimonial && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpandedTestimonial(null)}
          >
            <motion.div
              className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <AvatarImage
                    src={expandedTestimonial.avatar}
                    name={expandedTestimonial.name}
                    size="lg"
                    className="shadow-lg"
                    fallbackType="dicebear"
                  />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{expandedTestimonial.name}</h3>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{expandedTestimonial.location}</span>
                      <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                      <span>{expandedTestimonial.persona}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setExpandedTestimonial(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-5 h-5 ${i < expandedTestimonial.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} 
                  />
                ))}
              </div>

              {/* Full Testimonial Text */}
              <div className="mb-6">
                <p className="text-gray-700 leading-relaxed text-lg" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                  "{expandedTestimonial.text}"
                </p>
              </div>

              {/* Metadata */}
              <div className="flex items-center gap-6 text-sm text-gray-500 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  <span>{expandedTestimonial.booksRead}+ books read</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Member since {expandedTestimonial.memberSince}</span>
                </div>
                {expandedTestimonial.verified && (
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span>Verified Reader</span>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// Premium 2026 Testimonial Card Component with Enhanced Depth & Typography
function TestimonialCard({ testimonial, isActive, isAdjacent, position, onReadMore }) {
  // Refined text length calculation for optimal UX
  const isLongText = testimonial.text.length > 150;
  
  return (
    <motion.div 
      className={`w-80 testimonial-card-fixed bg-white/85 backdrop-blur-sm rounded-3xl border border-white/40 relative overflow-hidden flex flex-col ${
        isActive ? 'testimonial-card-active' : ''
      }`}
      style={{
        background: isActive 
          ? "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)"
          : "linear-gradient(135deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.82) 100%)",
        // Enhanced shadow system with stronger active card prominence
        boxShadow: isActive 
          ? "0 30px 80px rgba(0, 0, 0, 0.16), 0 12px 35px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)"
          : isAdjacent
            ? "0 18px 45px rgba(0, 0, 0, 0.1), 0 8px 25px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5)"
            : "0 10px 30px rgba(0, 0, 0, 0.08), 0 4px 15px rgba(0, 0, 0, 0.04)",
        // Critical: Fix edge/corner rendering
        transform: 'translateZ(0)',
        willChange: 'transform',
        backgroundClip: 'padding-box',
        borderRadius: '1.5rem', // Explicit border radius to ensure consistency
        isolation: 'isolate' // Create new stacking context to prevent bleed-through
      }}
    >
      {/* Enhanced Background Pattern with Depth */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-orange-50/30 via-transparent to-orange-100/20 pointer-events-none"
        style={{ borderRadius: '1.5rem' }}
      ></div>
      <div 
        className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/70 to-transparent"
        style={{ borderRadius: '1.5rem 1.5rem 0 0' }}
      ></div>
      
      {/* Active Card Radial Glow */}
      {isActive && (
        <div 
          className="absolute -inset-4 bg-gradient-radial from-orange-200/15 via-orange-100/8 to-transparent blur-xl pointer-events-none"
          style={{ borderRadius: '2rem' }}
        ></div>
      )}
      
      {/* Header Zone - Refined (55px) */}
      <div className="relative z-10 p-6 pb-2 flex-shrink-0 h-[55px]">
        <div className="flex items-start justify-between h-full">
          {/* Enhanced Verified Badge */}
          {testimonial.verified && (
            <motion.div 
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50/95 text-emerald-700 rounded-full text-xs font-semibold border border-emerald-200/70 backdrop-blur-sm shadow-sm"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
              whileHover={{ scale: 1.05, boxShadow: "0 4px 12px rgba(16, 185, 129, 0.15)" }}
            >
              <CheckCircle className="w-3.5 h-3.5" />
              <span>Verified</span>
            </motion.div>
          )}

          {/* Refined Quote Mark */}
          <motion.div 
            className="text-2xl font-serif text-orange-300/60 leading-none select-none"
            initial={{ opacity: 0, scale: 0.5, rotate: -15 }}
            animate={{ opacity: 1, scale: 1, rotate: -8 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
          >
            "
          </motion.div>
        </div>
      </div>

      {/* Rating Zone - Refined (35px) */}
      <div className="relative z-10 px-6 pb-1 flex-shrink-0 h-[35px]">
        <motion.div 
          className="flex items-center gap-1"
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 250 }}
        >
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0, rotate: -180 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ 
                delay: 0.4 + i * 0.08, 
                type: "spring", 
                stiffness: 300,
                damping: 15
              }}
              whileHover={{ scale: 1.15, rotate: 8 }}
            >
              <Star className={`w-4 h-4 transition-all duration-300 ${
                i < testimonial.rating 
                  ? 'text-amber-400 fill-amber-400 drop-shadow-sm filter brightness-110' 
                  : 'text-gray-200 fill-gray-200'
              }`} />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Content Zone - Enhanced Typography (250px/210px) */}
      <div className="relative z-10 px-8 flex-1 flex flex-col h-[250px] sm:h-[210px] max-h-[250px] sm:max-h-[210px] min-h-[250px] sm:min-h-[210px]">
        <motion.div 
          className="flex-1 relative overflow-hidden"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <motion.div
            className="relative h-full"
            initial={{ opacity: 1, y: 0 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="relative h-full overflow-hidden">
              {/* Enhanced Typography with Better Readability - Fixed Clipping */}
              <div className="w-full px-2">
                <p 
                  className={`text-gray-700 font-medium ${
                    isLongText ? 'line-clamp-4' : ''
                  }`}
                  style={{ 
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: '15px',
                    lineHeight: '1.75',
                    letterSpacing: '0.01em',
                    maxHeight: isLongText ? '7rem' : 'auto',
                    opacity: 1,
                    visibility: 'visible',
                    textAlign: 'left',
                    color: isActive ? '#374151' : '#4B5563',
                    padding: '0 8px'
                  }}
                >
                  {testimonial.text}
                </p>
              </div>
              
              {/* Enhanced Gradient Fade with Proper Clipping */}
              {isLongText && isActive && (
                <div 
                  className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white/98 via-white/85 to-transparent pointer-events-none"
                  style={{ borderRadius: '0 0 1.5rem 1.5rem' }}
                ></div>
              )}
            </div>
            
            {/* Modern 2026 CTA - Sleek & Minimal */}
            {isLongText && isActive && (
              <motion.div
                className="absolute bottom-4 left-8 right-8 backdrop-blur-sm"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <motion.button
                  onClick={onReadMore}
                  className="group relative inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-all duration-300 ease-out bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg px-3 py-2"
                  whileHover={{ x: 3 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Modern Underline Effect */}
                  <span className="relative">
                    Read more
                    <motion.div
                      className="absolute bottom-0 left-0 h-px bg-gradient-to-r from-gray-400 to-gray-600"
                      initial={{ width: 0 }}
                      whileHover={{ width: "100%" }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    />
                  </span>
                  
                  {/* Sleek Arrow Icon */}
                  <motion.div
                    className="flex items-center justify-center w-4 h-4 rounded-full bg-gray-100/50 group-hover:bg-gray-200/60 backdrop-blur-sm transition-colors duration-300"
                    whileHover={{ scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 15 }}
                  >
                    <svg 
                      className="w-2.5 h-2.5 text-gray-600 group-hover:text-gray-900 transition-colors duration-300" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2.5} 
                        d="M9 5l7 7-7 7" 
                      />
                    </svg>
                  </motion.div>
                </motion.button>
              </motion.div>
            )}
          </motion.div>
        </motion.div>

        {/* Refined Elegant Divider */}
        <motion.div 
          className="relative h-px my-5 overflow-hidden flex-shrink-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-200/70 to-transparent"></div>
          <motion.div 
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-orange-400 to-amber-400"
            initial={{ width: 0 }}
            animate={{ width: isActive ? 70 : 45 }}
            transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
          />
        </motion.div>
      </div>

      {/* Footer Zone - 2026 Modern UI Standards with Two-Row Metadata (85px) */}
      <div className="relative z-10 p-6 pt-0 flex-shrink-0 h-[85px]">
        <motion.div 
          className="flex flex-col gap-2 h-full"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, type: "spring", stiffness: 200 }}
        >
          {/* Avatar + Name Row */}
          <div className="flex items-center gap-4">
            {/* Sharp Avatar - No Effects */}
            <motion.div 
              className="relative flex-shrink-0"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              <AvatarImage
                src={testimonial.avatar}
                name={testimonial.name}
                size="md"
                className="shadow-md"
                fallbackType="dicebear"
              />
            </motion.div>

            {/* User Name - Emphasized */}
            <motion.h4 
              className="font-semibold text-gray-900 text-base leading-tight"
              style={{ fontWeight: 600 }}
              whileHover={{ x: 2 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {testimonial.name}
            </motion.h4>
          </div>

          {/* Metadata Section - Two Rows */}
          <div className="flex flex-col gap-1 ml-16">
            {/* Row 1: Location + Role */}
            <div className="flex items-center gap-2 text-xs leading-tight flex-wrap" style={{ opacity: 0.65 }}>
              {/* Location */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <MapPin className="w-3 h-3 flex-shrink-0 text-gray-500" />
                <span className="font-medium">{testimonial.location}</span>
              </div>
              
              {/* Separator */}
              <span className="text-gray-400 font-medium flex-shrink-0">•</span>
              
              {/* Role */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <Award className="w-3 h-3 flex-shrink-0 text-gray-500" />
                <span className="font-medium">{testimonial.persona}</span>
              </div>
            </div>

            {/* Row 2: Books Read + Member Since */}
            <div className="flex items-center gap-2 text-xs leading-tight flex-wrap" style={{ opacity: 0.65 }}>
              {/* Books Read */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <BookOpen className="w-3 h-3 flex-shrink-0 text-gray-500" />
                <span className="font-medium">{testimonial.booksRead}+ books</span>
              </div>
              
              {/* Separator */}
              <span className="text-gray-400 font-medium flex-shrink-0">•</span>
              
              {/* Member Since */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <Calendar className="w-3 h-3 flex-shrink-0 text-gray-500" />
                <span className="font-medium">Since {testimonial.memberSince}</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Enhanced Active Card Glow */}
      {isActive && (
        <motion.div 
          className="absolute inset-0 rounded-3xl bg-gradient-to-br from-orange-400/10 via-transparent to-amber-400/10 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        />
      )}

      {/* Subtle Position-Based Accent */}
      {!isActive && (
        <div 
          className={`absolute top-0 ${position === 'left' ? 'left-0' : 'right-0'} w-1 h-full bg-gradient-to-b ${
            position === 'left' 
              ? 'from-orange-300/40 to-transparent' 
              : 'from-amber-300/40 to-transparent'
          } pointer-events-none`}
        />
      )}
    </motion.div>
  );
}